import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import MessageModal from "../../components/shared/MessageModal";
import BlackwavePanel from "./BlackwavePanel";
import BlackwaveScope from "./BlackwaveScope";
import MidiKeyboard from "./MidiKeyboard";
import { modulatorWaveform, adsrEnvelope, modRateLabel, adsrTimeLabel } from "../../Data/products/blackwave/scopeEngines";
import "./SettingsEditor.css";
import "./BlackwaveEditor.css";
import { various, settingsEditor } from "../../Data/siteContent";
import mockResponse from "../../Data/products/blackwave/mockSysexResponse.json";

const PANEL_MANIFEST_URL = "/resources/products/blackwave/panel.json";

// Protocollo SysEx Blackwave (vedi firmware midi_handlers.cpp). Manufacturer ID
// 0x00 0x02 0x0D. I settaggi globali (NON mappati su CC) sono: canale RX (1..16) e
// duoEnvRetrigger (0/1), gestiti qui (READ/WRITE). I parametri (24 pot + 13 switch)
// viaggiano invece su MIDI CC e il cambio preset su Program Change: li gestisce il
// <BlackwavePanel/>, che all'apertura chiede un SysEx STATE DUMP (CMD 0x04) per
// allinearsi allo stato corrente del device (knob/switch/preset).
const SYSEX_HEADER = [0xf0, 0x00, 0x02, 0x0d];
const SYSEX_END = 0xf7;
const CMD_READ_REQUEST = 0x01;
const CMD_WRITE = 0x02;
const CMD_READ_RESPONSE = 0x03;
// STATE DUMP (0x04): chiede al device di rispedire tutti i CC + il PC del preset
// attivo → il <BlackwavePanel/> si riallinea (force resync). START CALIBRATION (0x06):
// avvia la routine di calibrazione VCO sul device (come tenere FN al boot).
const CMD_STATE_DUMP = 0x04;
const CMD_START_CALIBRATION = 0x06;
// READ RESPONSE: F0 00 02 0D 03 <ch> <duo> <verMaj> <verMin> F7 = 10 byte
const READ_RESPONSE_LENGTH = 10;
const READ_TIMEOUT_MS = 2000;

const DEFAULT_SETTINGS = {
  channel: "1", // 1..16 (il firmware memorizza direttamente 1..16, niente offset)
  duoEnvRetrigger: false,
};

function BlackwaveEditor({ feature, productId }) {
  const { usbDeviceName: deviceName, labels } = feature;
  const [searchParams] = useSearchParams();
  const devMode = searchParams.get("dev") === "true";

  const [midiOutputs, setMidiOutputs] = useState([]);
  const [output, setOutput] = useState(null);
  const [input, setInput] = useState(null);
  const [midiError, setMidiError] = useState("");
  const [showMidiError, setShowMidiError] = useState(false);
  const [firmwareVersion, setFirmwareVersion] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(false);
  // true quando il pannello ha ricevuto il dump dei parametri dall'hardware (STATE
  // DUMP). Finche' e' false l'editor (pannello + tastiera) resta oscurato/bloccato.
  const [panelSynced, setPanelSynced] = useState(false);
  // true dopo aver mandato START CALIBRATION: durante la routine il device è muto e
  // non risponde al MIDI, quindi l'editor va inibito tutto. Si sblocca da solo appena
  // il device torna a rispondere (prima READ RESPONSE, vedi effetto di polling).
  const [calibrating, setCalibrating] = useState(false);
  // valori correnti dei knob/switch del pannello (nome-control -> 0..127), esposti
  // da <BlackwavePanel/>. Alimentano gli specchietti (scope) affianco al pannello.
  const [panelValues, setPanelValues] = useState({});
  // forma del modulator (da MOD_SOURCE + MOD_RATE) e dei due EG (A/D/S/R). Ricalcolate
  // solo quando i pot rilevanti cambiano. Vedi scopeEngines.js.
  const modData = useMemo(
    () => modulatorWaveform(panelValues.MOD_SOURCE ?? 0),
    [panelValues.MOD_SOURCE]
  );
  const eg1Data = useMemo(
    () => adsrEnvelope(panelValues.EG1_ATTACK ?? 0, panelValues.EG1_DECAY ?? 0, panelValues.EG1_SUSTAIN ?? 0, panelValues.EG1_RELEASE ?? 0),
    [panelValues.EG1_ATTACK, panelValues.EG1_DECAY, panelValues.EG1_SUSTAIN, panelValues.EG1_RELEASE]
  );
  const eg2Data = useMemo(
    () => adsrEnvelope(panelValues.EG2_ATTACK ?? 0, panelValues.EG2_DECAY ?? 0, panelValues.EG2_SUSTAIN ?? 0, panelValues.EG2_RELEASE ?? 0),
    [panelValues.EG2_ATTACK, panelValues.EG2_DECAY, panelValues.EG2_SUSTAIN, panelValues.EG2_RELEASE]
  );

  const readTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const applyResponse = useCallback(data => {
    // data: [F0,00,02,0D,03, ch, duo, maj, min, F7]
    setSettings({
      channel: String(data[5]),
      duoEnvRetrigger: data[6] !== 0,
    });
    setFirmwareVersion(`v${data[7]}.${data[8]}`);
  }, []);

  const handleReadResponse = useCallback(
    data => {
      if (readTimeoutRef.current) {
        clearTimeout(readTimeoutRef.current);
        readTimeoutRef.current = null;
      }
      setIsLoading(false);
      setCalibrating(false);  // il device risponde di nuovo → calibrazione finita
      applyResponse(data);
    },
    [applyResponse]
  );

  const connectMidi = useCallback(() => {
    if (devMode) return;

    navigator
      .requestMIDIAccess({ sysex: true })
      .then(access => {
        const nextOutputs = [];
        let matchedOutput = null;
        let matchedInput = null;

        for (const out of access.outputs.values()) {
          nextOutputs.push(out);
          if (out.name.toUpperCase().includes(deviceName.toUpperCase())) {
            matchedOutput = out;
          }
        }
        for (const inp of access.inputs.values()) {
          if (inp.name.toUpperCase().includes(deviceName.toUpperCase())) {
            matchedInput = inp;
          }
        }

        setMidiOutputs(nextOutputs);
        if (matchedOutput) setOutput(matchedOutput);
        if (matchedInput) setInput(matchedInput);
      })
      .catch(error => {
        if (error && error.name === "NotAllowedError") {
          setMidiError(various.midiGrantError1);
        } else {
          setMidiError(error?.message || various.midiGrantError2);
        }
        setShowMidiError(true);
      });
  }, [deviceName, devMode]);

  useEffect(() => {
    connectMidi();
  }, [connectMidi]);

  useEffect(() => {
    if (!input) return;

    const onMidiMessage = event => {
      const data = event.data;
      const matches =
        data.length === READ_RESPONSE_LENGTH &&
        data[0] === 0xf0 &&
        data[1] === 0x00 &&
        data[2] === 0x02 &&
        data[3] === 0x0d &&
        data[4] === CMD_READ_RESPONSE;

      if (matches) {
        handleReadResponse(data);
      }
    };

    input.addEventListener("midimessage", onMidiMessage);
    return () => input.removeEventListener("midimessage", onMidiMessage);
  }, [input, handleReadResponse]);

  useEffect(() => {
    return () => {
      if (readTimeoutRef.current) clearTimeout(readTimeoutRef.current);
    };
  }, []);

  const handleChannelChange = value => setSettings(prev => ({ ...prev, channel: value }));
  const handleDuoChange = value => setSettings(prev => ({ ...prev, duoEnvRetrigger: value }));

  function buildWriteMessage() {
    const ch = parseInt(settings.channel, 10);
    const duo = settings.duoEnvRetrigger ? 1 : 0;
    return new Uint8Array([...SYSEX_HEADER, CMD_WRITE, ch, duo, SYSEX_END]);
  }

  function handleSaveToDevice() {
    const message = buildWriteMessage();
    if (devMode) {
      console.log(
        "DEV MODE — SysEx WRITE:",
        Array.from(message)
          .map(b => "0x" + b.toString(16).padStart(2, "0"))
          .join(" ")
      );
      return;
    }
    output.send(message);
  }

  // opts: { silent } non mostra il modale d'errore sul timeout (per il tentativo
  // automatico al connect); { onTimeout } callback chiamata allo scadere (per i retry).
  // NB: come onClick del bottone riceve l'evento come primo arg -> silent/onTimeout
  // restano undefined -> comportamento manuale con modale, invariato.
  const handleLoadFromDevice = useCallback(
    (opts = {}) => {
      const { silent = false, onTimeout } = opts;
      if (devMode) {
        handleReadResponse(mockResponse.data);
        return;
      }
      if (!output || !input) return;

      setIsLoading(true);
      const message = new Uint8Array([...SYSEX_HEADER, CMD_READ_REQUEST, SYSEX_END]);
      output.send(message);

      readTimeoutRef.current = setTimeout(() => {
        readTimeoutRef.current = null;
        setIsLoading(false);
        if (!silent) {
          setMidiError(settingsEditor.noResponseError);
          setShowMidiError(true);
        }
        if (onTimeout) onTimeout();
      }, READ_TIMEOUT_MS);
    },
    [devMode, output, input, handleReadResponse]
  );

  // Sync automatico al collegamento: appena il device e' pronto (output + input),
  // chiede i settaggi correnti (READ REQUEST) cosi' l'editor riflette lo stato reale
  // del synth senza premere "Load from device". Il pannello (knob/switch/preset) si
  // allinea per conto suo via STATE DUMP. Il device appena enumerato puo' non essere
  // pronto al primo invio: si ritenta in SILENZIO (niente modale) qualche volta;
  // appena arriva la READ RESPONSE il timeout viene azzerato e la catena si ferma.
  const autoLoadedInputRef = useRef(null);
  useEffect(() => {
    if (devMode) return;
    if (!output || !input) return;
    if (autoLoadedInputRef.current === input) return; // gia' avviato per questo input
    autoLoadedInputRef.current = input;
    let cancelled = false;
    let tries = 0;
    const attempt = () => {
      if (cancelled) return;
      tries += 1;
      handleLoadFromDevice({
        silent: true,
        onTimeout: () => {
          if (!cancelled && tries < 5) attempt();
        },
      });
    };
    attempt();
    return () => {
      cancelled = true;
    };
  }, [devMode, output, input, handleLoadFromDevice]);

  // Force resync: richiede al device uno STATE DUMP (tutti i CC + PC del preset
  // attivo). Il <BlackwavePanel/> è sempre in ascolto e si riallinea da solo alla
  // raffica in arrivo. Utile se knob/switch/preset sono andati fuori sync.
  function handleForceResync() {
    if (devMode || !output) return;
    output.send(new Uint8Array([...SYSEX_HEADER, CMD_STATE_DUMP, SYSEX_END]));
  }

  // Calibrate: avvia la routine di calibrazione VCO sul device. Durante la
  // calibrazione il synth è muto e non risponde al MIDI (~1 min). Conferma prima,
  // poi inibisce tutto l'editor (calibrating) finché il device non torna a rispondere.
  function handleCalibrate() {
    if (devMode || !output) return;
    const ok = window.confirm(settingsEditor.calibrateConfirm.replace("{device}", deviceName));
    if (!ok) return;
    output.send(new Uint8Array([...SYSEX_HEADER, CMD_START_CALIBRATION, SYSEX_END]));
    setCalibrating(true);
  }

  // Mentre calibra: il device ha RX/TX MIDI spenti, quindi va sondato. Ogni 2s manda
  // un READ REQUEST; appena arriva la READ RESPONSE (handleReadResponse) calibrating
  // torna false. Safety: sblocca comunque dopo un tetto, per non restare bloccati.
  useEffect(() => {
    if (devMode || !calibrating || !output) return;
    const poll = () => output.send(new Uint8Array([...SYSEX_HEADER, CMD_READ_REQUEST, SYSEX_END]));
    const id = setInterval(poll, 2000);
    const safety = setTimeout(() => setCalibrating(false), 180000);
    return () => {
      clearInterval(id);
      clearTimeout(safety);
    };
  }, [devMode, calibrating, output]);

  function handleSaveToFile() {
    const fileData = { settings };
    const blob = new Blob([JSON.stringify(fileData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${deviceName.toLowerCase()}_settings.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleLoadFromFile(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = e => {
      try {
        const fileData = JSON.parse(e.target.result);
        if (fileData.settings) {
          setSettings({ ...DEFAULT_SETTINGS, ...fileData.settings });
        }
      } catch {
        setMidiError("Invalid settings file.");
        setShowMidiError(true);
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }

  const isConnected = devMode || midiOutputs.some(e => e.name.toUpperCase().includes(deviceName.toUpperCase()));
  const canSend = devMode || (output && output.name.toUpperCase().includes(deviceName.toUpperCase()));
  const canLoad = devMode || (canSend && input);
  // L'editor e' utilizzabile solo quando e' connesso E ha caricato i parametri
  // correnti dall'hardware (dump ricevuto). In devMode e' sempre sbloccato.
  const editorReady = devMode || (isConnected && panelSynced);
  // Inibizione totale: fuori sync/non connesso OPPURE calibrazione in corso. Blocca
  // pannello, tastiera e tutti i controlli sotto (pulsanti + settings).
  const inhibited = !editorReady || calibrating;
  const overlayMsg = calibrating
    ? settingsEditor.calibrating.replace("{device}", deviceName)
    : (isConnected ? settingsEditor.syncing : settingsEditor.syncConnect).replace("{device}", deviceName);

  return (
    <div className="settings-editor__container">
      {devMode && <div className="settings-editor__dev-badge">{settingsEditor.devBadge}</div>}

      {/* Barra di stato in cima (prima del pannello): connessione + versione firmware
          al suo fianco. Quando non connesso mostra l'aiuto per collegarsi. */}
      <div className="bw-topbar">
        {isConnected ? (
          <>
            <span className="bw-topbar__status">
              {deviceName} {settingsEditor.connected}
            </span>
            {firmwareVersion && <span className="bw-topbar__fw">Firmware {firmwareVersion}</span>}
          </>
        ) : (
          <span className="bw-topbar__status">
            {deviceName} {settingsEditor.notConnected}{" "}
            {(() => {
              const parts = settingsEditor.notConnectedHelp.replace("{device}", deviceName).split("{link}");
              return (
                <>
                  {parts[0]}
                  <button type="button" className="settings-editor__inline-link" onClick={connectMidi}>
                    {settingsEditor.notConnectedHelpLinkText}
                  </button>
                  {parts[1]}
                </>
              );
            })()}
          </span>
        )}
      </div>

      {/* Pannello interattivo dal manifest PanelDesigner: sfondo SVG + knob/switch
          sovrapposti, che inviano/ricevono i MIDI CC (mappa in panelCcMap.js) e si
          riposizionano al load di un preset (CC in ingresso). */}
      {/* Pannello + tastiera inibiti (fuori sync / non connesso / calibrazione): solo
          i loro elementi vengono oscurati e resi non cliccabili (classe bw-inhibited su
          ciascuno), NON lo sfondo del wrapper. L'overlay porta solo il messaggio di
          stato (sfondo trasparente): il blocco click è sui singoli elementi. */}
      <div className="bw-sync-wrap">
        {/* Stage del pannello: il pannello resta centrato (max-width 880); gli
            specchietti (scope) sono posizionati affianco, lato basso — modulator a
            sinistra, i due EG impilati a destra. Rispecchiano i pot in tempo reale. */}
        <div className="bw-panel-stage">
          <div className={inhibited ? "bw-inhibited" : undefined}>
            <BlackwavePanel
              manifestUrl={PANEL_MANIFEST_URL}
              output={output}
              input={input}
              channel={parseInt(settings.channel, 10)}
              devMode={devMode}
              onLoadedChange={setPanelSynced}
              onValuesChange={setPanelValues}
            />
          </div>

          {/* Specchietti stile pulsante giallo (classi globali), affianco al pannello,
              centrati verticalmente coi rispettivi knob. In ALTO a sinistra i tempi
              (rate del mod source; A/D/R degli EG allineati alla loro porzione); in
              BASSO a sinistra il nome del grafico. Testo nero con alone giallo che
              occlude la linea. */}
          <div className="settings-editor__button bw-scope-box bw-scope--mod">
            <span className="bw-scope__txt bw-scope__txt--tl">{modRateLabel(panelValues.MOD_RATE ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__name">MOD SOURCE</span>
            <BlackwaveScope data={modData} className="bw-scope__cv" ariaLabel="Modulator waveform" />
          </div>
          <div className="settings-editor__button bw-scope-box bw-scope--egvcf">
            <span className="bw-scope__txt bw-scope__t--a">{adsrTimeLabel(panelValues.EG2_ATTACK ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__t--d">{adsrTimeLabel(panelValues.EG2_DECAY ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__t--r">{adsrTimeLabel(panelValues.EG2_RELEASE ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__name">EG VCF</span>
            <BlackwaveScope data={eg2Data} className="bw-scope__cv" ariaLabel="EG2 (VCF) shape" />
          </div>
          <div className="settings-editor__button bw-scope-box bw-scope--egvca">
            <span className="bw-scope__txt bw-scope__t--a">{adsrTimeLabel(panelValues.EG1_ATTACK ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__t--d">{adsrTimeLabel(panelValues.EG1_DECAY ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__t--r">{adsrTimeLabel(panelValues.EG1_RELEASE ?? 0)}</span>
            <span className="bw-scope__txt bw-scope__name">EG VCA</span>
            <BlackwaveScope data={eg1Data} className="bw-scope__cv" ariaLabel="EG1 (VCA) shape" />
          </div>
        </div>

        {/* Tastiera MIDI di test (25 tasti): manda NoteOn/Off al device sul canale RX */}
        <div className={inhibited ? "bw-inhibited" : undefined}>
          <MidiKeyboard output={output} channel={parseInt(settings.channel, 10)} devMode={devMode} />
        </div>

        {inhibited && (
          <div className="bw-sync-overlay" role="status" aria-live="polite">
            <div className="bw-sync-overlay__msg">{overlayMsg}</div>
          </div>
        )}
      </div>

      {isConnected && (
        <div className="settings-editor__section">
          {settingsEditor.connectedHelp.replaceAll("{device}", deviceName)}
            <br />
            {settingsEditor.connectedHelp2.replaceAll("{device}", deviceName)}
            <br />
            <br />
            <div className="settings-editor__button-group" style={{ marginBottom: "16px" }}>
              <button className="settings-editor__button" onClick={handleForceResync} disabled={!canSend || calibrating}>
                {settingsEditor.buttonForceResync}
              </button>
              <button className="settings-editor__button" onClick={handleCalibrate} disabled={!canSend || calibrating}>
                {settingsEditor.buttonCalibrate}
              </button>
            </div>
            <div className="settings-editor__button-group">
              <button className="settings-editor__button" onClick={handleLoadFromDevice} disabled={!canLoad || isLoading || calibrating}>
                {isLoading
                  ? settingsEditor.buttonLoading
                  : settingsEditor.buttonLoadDevice.replace("{device}", deviceName)}
              </button>
              <button className="settings-editor__button" onClick={handleSaveToDevice} disabled={!canSend || calibrating}>
                {settingsEditor.buttonSaveDevice.replace("{device}", deviceName)}
              </button>
              <button className="settings-editor__button" onClick={() => fileInputRef.current.click()} disabled={calibrating}>
                {settingsEditor.buttonLoadFile}
              </button>
              <button className="settings-editor__button" onClick={handleSaveToFile} disabled={calibrating}>
                {settingsEditor.buttonSaveFile}
              </button>
            </div>
            <input ref={fileInputRef} type="file" accept=".json" onChange={handleLoadFromFile} hidden />
          </div>
        )}

      {/* Settings NON mappati su CC: canale RX + modalità DUO. Due box affiancati,
          vicini e centrati (flex, niente table che li allargava). */}
      <div className="bw-settings-row">
        <div className="td-settings-table">
          <div className="title-td-table-settings">{labels.sectionMidi}</div>
          <div className="settings-container-forms">
            <div className="settings-editor__form-group">
              <label>
                {labels.labelChannel}
                <select
                  className="settings-editor__select"
                  value={settings.channel}
                  onChange={e => handleChannelChange(e.target.value)}
                  disabled={calibrating}
                >
                  {Array.from({ length: 16 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </div>

        <div className="td-settings-table">
          <div className="title-td-table-settings">{labels.sectionDuo}</div>
          <div className="settings-container-forms">
            <div className="settings-editor__form-group">
              <label>
                {labels.labelDuoRetrigger}
                <select
                  className="settings-editor__select"
                  value={settings.duoEnvRetrigger ? "1" : "0"}
                  onChange={e => handleDuoChange(e.target.value === "1")}
                  disabled={calibrating}
                >
                  <option value="0">{labels.duoLegato}</option>
                  <option value="1">{labels.duoRetrigger}</option>
                </select>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer aggiornamento firmware: in fondo alla pagina */}
      {(() => {
        const parts = settingsEditor.disclaimer.replace("{device}", deviceName).split("{link}");
        return (
          <div className="settings-editor__disclaimer bw-disclaimer-bottom">
            {parts[0]}
            <Link to={`/products/${productId}/updates`}>{settingsEditor.disclaimerLinkText}</Link>
            {parts[1]}
          </div>
        );
      })()}

      <MessageModal
        isVisible={showMidiError}
        title="Error"
        message={midiError}
        type="error"
        closeLabel="Close"
        onClose={() => setShowMidiError(false)}
      />
    </div>
  );
}

export default BlackwaveEditor;
